import { useEffect, useState } from "react";
import { CircleCheck, CircleDashed, Info, Loader2, MessageSquareText, Send } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";
import { SITE_BASE_URL } from "@/lib/site";

/* WhatsApp Business (Cloud API di Meta): preparazione opzionale.
   Nessun messaggio automatico finché l'admin non configura Meta e attiva l'integrazione. */
export default function WhatsAppTab({ flash }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState("");

  const load = () => {
    api
      .get("/admin/whatsapp")
      .then(({ data }) => {
        setCfg(data);
        setForm({
          enabled: !!data.enabled,
          phoneNumberId: data.phoneNumberId || "",
          accessToken: "",
          appSecret: data.appSecret || "",
          verifyToken: data.verifyToken || "",
          notifyBusinessOnRequest: !!data.notifyBusinessOnRequest,
          sendConfirmationOnApprove: !!data.sendConfirmationOnApprove,
          sendReminderDayBefore: !!data.sendReminderDayBefore,
        });
      })
      .catch((e) => flash(apiErrorDetail(e), false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/whatsapp", form);
      flash(data.enabled ? "WhatsApp attivato" : "Configurazione salvata (WhatsApp disattivato)");
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) {
      flash("Inserisci un numero di telefono per la prova", false);
      return;
    }
    setTesting(true);
    try {
      await api.post(`/admin/whatsapp/test?to_phone=${encodeURIComponent(testPhone.trim())}`);
      flash("Messaggio di prova inviato");
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setTesting(false);
    }
  };

  if (!cfg || !form) return <p className="text-charcoal/50 text-sm py-10">Caricamento…</p>;

  const enabled = !!cfg.enabled;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl lg:text-3xl flex items-center gap-3">
          <MessageSquareText size={24} className="text-deeptaupe" /> WhatsApp Business (opzionale)
        </h2>
        <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">
          Integrazione ufficiale di Meta (Cloud API). Finché non la attivi tu, nessun messaggio automatico viene mai inviato:
          restano attivi i pulsanti WhatsApp manuali e le notifiche email.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="bg-white rounded-[24px] shadow-soft p-6" data-testid="whatsapp-now">
          <p className="font-medium text-sm flex items-center gap-2"><CircleCheck size={16} className="text-[#1f7a4d]" /> Funziona già, subito</p>
          <ul className="text-[13px] text-charcoal/70 mt-3 space-y-1.5 list-disc ml-4">
            <li>Pulsanti WhatsApp manuali per ogni appuntamento (conferma, rifiuto, contatto).</li>
            <li>Email immediata allo studio per ogni nuova richiesta, con bozza WhatsApp già scritta.</li>
            <li>Email mattutina con i promemoria WhatsApp per gli appuntamenti di domani.</li>
          </ul>
        </div>
        <div className="bg-white rounded-[24px] shadow-soft p-6" data-testid="whatsapp-needed">
          <p className="font-medium text-sm flex items-center gap-2"><CircleDashed size={16} className="text-taupe" /> Serve invece un account esterno</p>
          <ul className="text-[13px] text-charcoal/70 mt-3 space-y-1.5 list-disc ml-4">
            <li>Account <b>Meta for Developers</b> con app WhatsApp Business (gratuito in sviluppo; i messaggi di utilità hanno costi Meta in produzione).</li>
            <li><b>Phone Number ID</b> e <b>token</b> (consigliato token di sistema con vita lunga).</li>
            <li><b>Template approvati</b> da Meta: <code className="text-[11px] bg-ivory rounded px-1">appointment_request</code>, <code className="text-[11px] bg-ivory rounded px-1">appointment_confirmation</code>, <code className="text-[11px] bg-ivory rounded px-1">appointment_reminder</code> (categoria utilità, localizzazione it).</li>
            <li><b>Consenso del paziente</b> a ricevere messaggi (casella dedicata nel modulo di prenotazione, attivata solo quando questa integrazione è attiva).</li>
            <li>Webhook HTTPS con firma verificata (qui a fianco, pronto).</li>
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-[24px] shadow-soft p-6" data-testid="whatsapp-config">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <p className="font-display text-xl">Configurazione Meta</p>
          <span className={`text-[11px] uppercase tracking-widest2 font-medium rounded-full px-3 py-1.5 ${enabled ? "bg-[#1f7a4d]/10 text-[#1f7a4d]" : "bg-taupe/20 text-charcoal/60"}`} data-testid="whatsapp-state">
            {enabled ? "Attivo" : "Disattivato"}
          </span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 text-sm">
          <label className="block" htmlFor="wa-phoneid">
            <span className="font-medium">Phone Number ID di Meta</span>
            <input id="wa-phoneid" type="text" value={form.phoneNumberId} onChange={(e) => setForm({ ...form, phoneNumberId: e.target.value })} data-testid="whatsapp-phone-id" className="mt-1.5 w-full rounded-2xl border border-taupe/40 px-4 py-3 bg-transparent" placeholder="es. 123456789012345" />
          </label>
          <label className="block" htmlFor="wa-token">
            <span className="font-medium">Access token {cfg.hasAccessToken ? "(salvato: lascia vuoto per tenerlo)" : "di Meta"}</span>
            <input id="wa-token" type="password" value={form.accessToken} onChange={(e) => setForm({ ...form, accessToken: e.target.value })} data-testid="whatsapp-token" className="mt-1.5 w-full rounded-2xl border border-taupe/40 px-4 py-3 bg-transparent" placeholder={cfg.hasAccessToken ? "••••••••" : "token di sistema"} />
            <span className="block text-charcoal/60 text-[13px] mt-1">
              Resta solo sul server, mai mostrato di nuovo.
              {cfg.hasAccessToken && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, clearAccessToken: true, accessToken: "" })}
                  data-testid="whatsapp-clear-token"
                  className="ml-2 underline underline-offset-2 text-red-600 hover:text-red-700"
                >
                  Rimuovi il token salvato
                </button>
              )}
            </span>
          </label>
          <label className="block" htmlFor="wa-secret">
            <span className="font-medium">App secret (per firmare i webhook)</span>
            <input id="wa-secret" type="password" value={form.appSecret} onChange={(e) => setForm({ ...form, appSecret: e.target.value })} data-testid="whatsapp-app-secret" className="mt-1.5 w-full rounded-2xl border border-taupe/40 px-4 py-3 bg-transparent" />
          </label>
          <label className="block" htmlFor="wa-verify">
            <span className="font-medium">Verify token dei webhook</span>
            <input id="wa-verify" type="text" value={form.verifyToken} onChange={(e) => setForm({ ...form, verifyToken: e.target.value })} data-testid="whatsapp-verify-token" className="mt-1.5 w-full rounded-2xl border border-taupe/40 px-4 py-3 bg-transparent" />
            <span className="block text-charcoal/60 text-[13px] mt-1">URL webhook da inserire su Meta: <code className="text-[11px] break-all">{SITE_BASE_URL}/api/webhooks/whatsapp</code></span>
          </label>
          <label className="block sm:col-span-2" htmlFor="wa-bizphone">
            <span className="font-medium">Numero dello studio per l'avviso di nuove richieste</span>
            <input id="wa-bizphone" type="text" value={form.businessNotifyPhone || ""} onChange={(e) => setForm({ ...form, businessNotifyPhone: e.target.value })} data-testid="whatsapp-business-phone" className="mt-1.5 w-full rounded-2xl border border-taupe/40 px-4 py-3 bg-transparent" placeholder="+39 …" />
            <span className="block text-charcoal/60 text-[13px] mt-1">Richiede il template <code className="text-[11px]">appointment_request</code> approvato.</span>
          </label>
        </div>

        <div className="space-y-3 mt-5 text-sm">
          <label className="flex items-center justify-between gap-4" htmlFor="wa-enable">
            <span className="flex-1"><b>Attiva l'invio automatico</b><span className="block text-charcoal/60 text-[13px]">Richiede Phone Number ID, token e template approvati da Meta. Finché è spento, nessun messaggio automatico.</span></span>
            <button id="wa-enable" role="switch" aria-checked={form.enabled} data-testid="whatsapp-enable" onClick={() => setForm({ ...form, enabled: !form.enabled })} className={`relative w-12 h-7 rounded-full transition-colors duration-300 shrink-0 ${form.enabled ? "bg-deeptaupe" : "bg-taupe/40"}`}>
              <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform duration-300 ${form.enabled ? "translate-x-5" : ""}`} />
            </button>
          </label>
          <button
            onClick={save}
            disabled={busy}
            data-testid="whatsapp-save"
            className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3.5 hover:bg-charcoal transition-colors disabled:opacity-60"
          >
            {busy && <Loader2 size={14} className="animate-spin" />} Salva configurazione
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[24px] shadow-soft p-6" data-testid="whatsapp-test-card">
        <p className="font-medium text-sm flex items-center gap-2"><Send size={15} className="text-deeptaupe" /> Messaggio di prova</p>
        <p className="text-[13px] text-charcoal/60 mt-1 mb-3">Funziona solo con l'integrazione attiva e un template approvato: invia al numero indicato il template di conferma.</p>
        <div className="flex flex-wrap gap-2">
          <input type="text" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+39 …" data-testid="whatsapp-test-phone" className="flex-1 min-w-[180px] rounded-2xl border border-taupe/40 px-4 py-3 text-sm bg-transparent" />
          <button onClick={sendTest} disabled={testing || !enabled} data-testid="whatsapp-test-send" className="inline-flex items-center gap-2 rounded-full border border-deeptaupe/40 text-deeptaupe text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 hover:bg-peach transition-colors disabled:opacity-40">
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Invia prova
          </button>
        </div>
        <p className="text-[12px] text-charcoal/50 mt-3 flex items-start gap-1.5"><Info size={13} className="mt-0.5 shrink-0" /> I messaggi partono solo a utenti che hanno dato il consenso nel modulo di prenotazione; nessun invio automatico senza configurazione esplicita.</p>
      </div>
    </div>
  );
}
